import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useSettingStore } from '@/stores'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Copy, Save, Eye, EyeOff } from 'lucide-react'
import { toast } from 'sonner'
import { config } from '@/lib/config'

const LineSetting = observer(() => {
  const { tenantId } = useParams<{ tenantId: string }>()
  const settingStore = useSettingStore()
  const [isEditing, setIsEditing] = useState(false)
  const [showSecret, setShowSecret] = useState(false)
  const [showToken, setShowToken] = useState(false)
  const [formData, setFormData] = useState({
    channel_id: '',
    channel_secret: '',
    long_term_token: '',
  })

  const webhookUrl = `${config.apiBaseUrl}/v1/tenants/${tenantId}/line/messaging`

  const loadLineSetting = async () => {
    const success = await settingStore.getLineSetting(tenantId!)
    if (success && settingStore.lineSetting) {
      setFormData({
        channel_id: settingStore.lineSetting.channel_id || '',
        channel_secret: settingStore.lineSetting.channel_secret || '',
        long_term_token: settingStore.lineSetting.long_term_token || '',
      })
    }
  }

  useEffect(() => {
    if (tenantId) {
      loadLineSetting()
    }
  }, [tenantId])

  const handleSave = async () => {
    const success = await settingStore.updateLineSetting(tenantId!, formData)
    if (success) {
      setIsEditing(false)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    if (settingStore.lineSetting) {
      setFormData({
        channel_id: settingStore.lineSetting.channel_id || '',
        channel_secret: settingStore.lineSetting.channel_secret || '',
        long_term_token: settingStore.lineSetting.long_term_token || '',
      })
    }
  }

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl)
    toast.success('Webhook URLをコピーしました')
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">LINE設定</h1>
          <p className="text-muted-foreground">
            LINE Messaging APIの設定を行います
          </p>
        </div>
      </div>

      <Tabs defaultValue="basic" className="space-y-4">
        <TabsList>
          <TabsTrigger value="basic">基本設定</TabsTrigger>
          <TabsTrigger value="webhook">Webhook設定</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>LINE Messaging API設定</CardTitle>
              <CardDescription>
                LINE Developers Consoleから取得した認証情報を設定してください
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="channel_id">チャネルID</Label>
                <Input
                  id="channel_id"
                  value={formData.channel_id}
                  onChange={(e) => setFormData({ ...formData, channel_id: e.target.value })}
                  disabled={!isEditing}
                  placeholder="1234567890"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="channel_secret">チャネルシークレット</Label>
                <div className="relative">
                  <Input
                    id="channel_secret"
                    type={showSecret ? "text" : "password"}
                    value={formData.channel_secret}
                    onChange={(e) => setFormData({ ...formData, channel_secret: e.target.value })}
                    disabled={!isEditing}
                    placeholder="********************************"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 py-2"
                    onClick={() => setShowSecret(!showSecret)}
                  >
                    {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="long_term_token">チャネルアクセストークン（長期）</Label>
                <div className="relative">
                  <Input
                    id="long_term_token"
                    type={showToken ? "text" : "password"}
                    value={formData.long_term_token}
                    onChange={(e) => setFormData({ ...formData, long_term_token: e.target.value })}
                    disabled={!isEditing}
                    placeholder="********************************"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 py-2"
                    onClick={() => setShowToken(!showToken)}
                  >
                    {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                {isEditing ? (
                  <>
                    <Button variant="outline" onClick={handleCancel}>
                      キャンセル
                    </Button>
                    <Button onClick={handleSave}>
                      <Save className="mr-2 h-4 w-4" />
                      保存
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => setIsEditing(true)}>
                    編集
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="webhook" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Webhook設定</CardTitle>
              <CardDescription>
                LINE Developers ConsoleのWebhook設定に以下のURLを設定してください
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertDescription>
                  LINE Developers ConsoleのMessaging API設定画面で、以下のWebhook URLを設定し、「Webhookの利用」をONにしてください。
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label>Webhook URL</Label>
                <div className="flex gap-2">
                  <Input
                    value={webhookUrl}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={copyWebhookUrl}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>設定手順</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                <li>LINE Developers Consoleにログイン</li>
                <li>プロバイダーとチャネルを作成（Messaging API）</li>
                <li>基本設定からチャネルID、チャネルシークレットを取得</li>
                <li>Messaging API設定からチャネルアクセストークン（長期）を発行</li>
                <li>上記の情報を「基本設定」タブに入力して保存</li>
                <li>Webhook URLをLINE Developers Consoleに設定</li>
                <li>Webhookの利用をONに設定</li>
                <li>応答メッセージをOFFに設定</li>
              </ol>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
})

export default LineSetting